import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';

import BtcIcon from '../assets/carousel/btc.svg';
import rankingIcon from '../assets/carousel/ranking.svg';

import solanaIcon from '../assets/carousel/solana.svg';

import powerpointIcon from '../assets/carousel/powerpoint.svg';

import whaleIcon from '../assets/carousel/whale.svg';
import newsIcon from '../assets/carousel/news.svg';
import hornIcon from '../assets/carousel/horn.svg';

import twitterIcon from '../assets/carousel/x.svg';

import chainIcon from '../assets/carousel/chain.svg';
import walletIcon from '../assets/carousel/wallet.svg';
import developerIcon from '../assets/carousel/dev.svg';
import etheriumIcon from '../assets/carousel/eth.svg';
import analysisIcon from '../assets/carousel/analysis.svg';
import growthIcon from '../assets/carousel/growth.svg';
import scaleIcon from '../assets/carousel/scale.svg';
import governmentIcon from '../assets/carousel/gov.svg';


const BLUE_BORDER = 'rgba(18, 157, 232, 0.25)';
const BLUE_GLOW   = '0 6px 18px rgba(18, 157, 232, 0.12)';

const QueryCarousel = ({ onItemClick }) => {
  // Each row's container ref
  const topRef = useRef(null);
  const middleRef = useRef(null);
  const bottomRef = useRef(null);

  // requestAnimationFrame ID, so we can cancel on unmount
  const rafIdRef = useRef(null);

  // Starting positions for top, middle, bottom
  let topPos = 0;
  let middlePos = -1000;
  let bottomPos = 0;

  useEffect(() => {
    function animate() {
      // Move them slightly each frame
      topPos -= 0.1;      // top row drifts left
      middlePos += 0.1;   // middle row drifts right
      bottomPos -= 0.1;   // bottom row drifts left

      // Wrap around so we get infinite scrolling
      if (topPos < -2400) topPos = 0;
      if (middlePos > 0) middlePos = -1000;
      if (bottomPos < -2400) bottomPos = 0;

      // Update each container's transform directly
      if (topRef.current) {
        topRef.current.style.transform = `translateX(${topPos}px)`;
      }
      if (middleRef.current) {
        middleRef.current.style.transform = `translateX(${middlePos}px)`;
      }
      if (bottomRef.current) {
        bottomRef.current.style.transform = `translateX(${bottomPos}px)`;
      }

      // Schedule next frame
      rafIdRef.current = requestAnimationFrame(animate);
    }

    // Start
    rafIdRef.current = requestAnimationFrame(animate);

    // Clean up on unmount: cancel the animation
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  // Hard-coded carousel item sets with their corresponding icons
  const carouselItems = [
    // Top row
    [
      {
        id: 1,
        text: 'Rank cryptos by momentum, sentiment, and on-chain strength',
        prompt: 'Generate a dashboard ranking the top 20 cryptocurrencies by a composite score combining: 30-day price momentum, social sentiment trend (7-day change), and on-chain health (active address growth + exchange outflows). Weight each factor equally. Flag assets where on-chain metrics are strengthening but price and sentiment haven\'t caught up yet.',
        icon: rankingIcon
      },
      {
        id: 2,
        text: 'Deep dive on Solana with whale flows and network growth',
        prompt: 'Create a research report on Solana covering: price performance across multiple timeframes, network growth metrics (active addresses, new addresses, daily transactions) with 90-day trends, whale wallet accumulation patterns, and exchange inflow/outflow balance. Layer in technical support/resistance levels and recent sentiment shifts. Generate a one-page PDF summary with the key on-chain signals and bull/bear case.',
        icon: solanaIcon
      },
      {
        id: 3,
        text: 'Build a slide deck on crypto portfolio construction',
        prompt: 'Generate an 8-slide PowerPoint on crypto portfolio construction for someone allocating 5-15% of their portfolio to digital assets. Include: risk/return characteristics vs traditional assets, large cap vs alt allocation tradeoffs, rebalancing frequency analysis, correlation benefits during different market regimes, position sizing frameworks, and a sample model portfolio with rationale for each holding.',
        icon: powerpointIcon
      },
      /*
      {
        id: 4,
        text: 'Which cryptos are whales quietly accumulating?',
        prompt: 'Create a research report analyzing whale wallet behavior across the top 30 cryptocurrencies over the past 21 days. Identify: assets with net accumulation by wallets holding >$1M, exchange outflow trends by holder cohort, and tokens where whale accumulation diverges from retail sentiment. Rank by conviction signal strength and explain what the smart money positioning suggests.',
        icon: whaleIcon
      }, 
      */
      {
        id: 5,
        text: 'Crypto news feed with sentiment signals',
        prompt: 'Generate a news feed covering recent cryptocurrency developments. For each story, include sentiment classification and the specific tokens impacted. Identify the dominant market narratives and any sentiment shifts emerging from the news flow.',
        icon: newsIcon
      },
      /*
      {
        id: 6,
        text: 'Scan for crypto breakouts with volume confirmation',
        prompt: 'Create a dashboard scanning the top 30 cryptocurrencies for high-probability technical setups. Filter for: breakouts from consolidation with above-average volume, bullish RSI divergences on the daily timeframe, and MACD crossovers confirmed by price action. For each setup, show the pattern, entry zone, stop loss level, and target based on measured move.',
        icon: hornIcon
      },
      */
    ],
    // Middle row
    [
      /*
      {
        id: 7,
        text: 'What\'s driving crypto sentiment on Twitter?',
        prompt: 'Generate a Twitter sentiment feed for crypto markets. Identify trending tokens and narratives, notable sentiment shifts from the prior 48 hours, and emerging themes gaining traction. Surface signal over noise.',
        icon: twitterIcon
      },
      */
      {
        id: 8,
        text: 'Compare Layer 1 chains on network growth and exchange flows',
        prompt: 'Create a research report comparing Ethereum, Solana, Avalanche, and Polygon on on-chain fundamentals. Analyze: 30/60/90-day active address growth rates, new wallet creation trends, transaction volume momentum, and net exchange flows (accumulation vs distribution). Generate a dashboard visualizing each metric\'s trajectory. Rank the chains by on-chain momentum and identify which is showing the strongest fundamental improvement relative to price action.',
        icon: chainIcon
      },
      /*
      {
        id: 9,
        text: 'Backtest a momentum-rebalanced crypto portfolio',
        prompt: 'Using the coding sandbox, backtest a dynamic crypto portfolio strategy: each month, allocate equally to the top 5 assets by 30-day momentum from the universe of top 20 cryptos by market cap. Run the backtest over the past 3 years. Generate a dashboard showing: cumulative returns vs buy-and-hold BTC and ETH, max drawdown, Sharpe ratio, monthly turnover, and a chart of rolling 12-month returns.',
        icon: walletIcon
      },
      */
      {
        id: 10,
        text: 'Rank blockchains by developer momentum',
        prompt: 'Generate a PDF report ranking Layer 1 blockchains by developer activity signals. Include: GitHub commits (30/90-day), unique active contributors, new repository creation, and 6-month developer retention rates. Compare Ethereum, Solana, Avalanche, Polygon, and Sui. Visualize the trends and identify chains where developer momentum is accelerating vs decelerating. Explain why developer activity is a leading indicator for investors.',
        icon: developerIcon
      },
      /*
      {
        id: 11,
        text: 'Build me a dashboard to analyze Ethereum whale accumulation vs price',
        prompt: 'Build a custom dashboard analyzing the relationship between Ethereum whale accumulation and price movements. Include: a dual-axis chart showing ETH price and whale wallet balance changes over 180 days, a scatter plot of weekly accumulation vs subsequent 14-day returns, exchange inflow/outflow trends segmented by wallet size, and a summary of what whale behavior currently suggests about near-term direction.',
        icon: etheriumIcon
      },
      */
      {
        id: 12,
        text: 'NVIDIA earnings preview with scenario analysis',
        prompt: 'Create a research report on NVIDIA ahead of earnings. Include: consensus revenue and EPS estimates with whisper numbers if available, key metrics to watch (data center growth, gross margins, guidance), historical earnings reaction patterns (average move, hit rate on beats), current valuation vs 5-year range, and technical levels. Build a scenario analysis showing bull/base/bear cases with price targets for each.',
        icon: analysisIcon
      },
    ],
    // Bottom row
    [
      {
        id: 13,
        text: 'Track crypto-correlated stocks with rolling beta to Bitcoin',
        prompt: 'Generate a dashboard tracking equities with high crypto correlation: MSTR, COIN, MARA, RIOT, NVDA, and AMD. Show: YTD performance, 30-day rolling beta to Bitcoin, 90-day correlation matrix with BTC and ETH, and relative strength rankings. Identify which names offer the highest and lowest leveraged exposure to crypto moves right now.',
        icon: growthIcon
      },
      {
        id: 14,
        text: 'MicroStrategy vs ETFs: which is the better Bitcoin trade?',
        prompt: 'Create a research report and 5-slide PowerPoint comparing MicroStrategy to spot Bitcoin ETFs (IBIT, FBTC). Analyze: NAV premium/discount history and current level, implied BTC per share and how dilution affects it, leverage and balance sheet risks, expense and tracking differences, and historical beta/volatility vs spot BTC. Provide a decision framework for when to own each vehicle.',
        icon: BtcIcon
      },
      /*
      {
        id: 15,
        text: 'Analyze how crypto correlations shift in risk-off markets',
        prompt: 'Analyze how correlations between Bitcoin, Ethereum, and traditional assets (SPY, QQQ, GLD, TLT) change during different market regimes. Identify periods where VIX exceeded 25 as risk-off environments. Calculate rolling 30-day correlation matrices for risk-on vs risk-off periods. Generate a dashboard showing: correlation heatmaps for each regime, time series of BTC-SPY correlation with VIX overlay, and a summary of whether crypto provides diversification when you need it most.',
        icon: developerIcon
      },
      */
      {
        id: 16,
        text: 'What\'s the optimal crypto allocation for a traditional portfolio?',
        prompt: 'Using the coding sandbox, run a mean-variance optimization to find the optimal Bitcoin and Ethereum allocation for a portfolio also holding US equities (SPY), bonds (AGG), and gold (GLD). Use the past 5 years of data. Generate an efficient frontier chart showing risk/return tradeoffs at different crypto weightings (0%, 5%, 10%, 15%, 20%). Calculate the Sharpe-optimal allocation and show how max drawdown changes as crypto weighting increases. Create a research report explaining the results with the code appendixed.',
        icon: scaleIcon
      },
      /*
      {
        id: 17,
        text: 'How do Fed rate decisions actually impact crypto?',
        prompt: 'Generate a dashboard analyzing crypto performance around FOMC decisions over the past 3 years. Show: average BTC and ETH returns in the 7 days before and after rate decisions, performance segmented by hike/cut/hold, correlation between crypto and rate expectations (Fed funds futures), and current positioning context. Include a chart overlaying BTC price with the Fed\'s policy path. Summarize what the data suggests for the current cycle.',
        icon: governmentIcon
      },
      */
    ],
  ];

  // Handle an item click
  const handleItemClick = (item) => {
    if (onItemClick) {
      onItemClick(item.prompt);
    }
  };

  function renderLayer(items, refKey, prefix) {
    return (
      <Box 
        sx={{ 
          position: 'relative', 
          height: 100,
          mb: 0, 
          overflowY: 'visible',
          overflowX: 'hidden',
          background: 'none',
        }}
      >
        {/* Left fade effect */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '70px',
            background: 'linear-gradient(to right, rgba(251,251,251,1) 0%, rgba(255,255,255,0) 100%)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
        
        {/* Right fade effect */}
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '70px',
            background: 'linear-gradient(to left, rgba(251,251,251,1) 0%, rgba(255,255,255,0) 100%)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
        
        <Box
          ref={refKey}
          sx={{
            position: 'absolute',
            display: 'flex',
            gap: 2,
            overflow: 'visible',
            mt: 0.5,
          }}
        >
          {[...items, ...items, ...items, ...items].map((obj, idx) => {
            const baseBorder = obj.cached ? `1px solid ${BLUE_BORDER}` : '1px solid rgba(162, 171, 176, 0.2)';
            // const baseShadow = obj.cached ? `${BLUE_GLOW}` : 'none';
            const baseBackground = obj.cached ? 'rgba(18, 157, 232, 0.035)' : 'rgba(255, 255, 255, 0.2)';
            return (
              <Box
                key={`${prefix}-${obj.id}-${idx}`}
                sx={{
                  background: baseBackground,
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: baseBorder,
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  pr: 2,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 'fit-content',
                  maxWidth: 300,
                  height: 80,
                  overflow: 'hidden',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
                  '&:hover': { 
                    background: 'rgba(18, 157, 232, 0.1)',
                    boxShadow: BLUE_GLOW,
                    transform: 'translateY(-2px)',
                    border: `1px solid ${BLUE_BORDER}`,
                  },
                }}
                onClick={() => handleItemClick(obj)}
              > 
               {/* Icon */}
                <Box
                  component="img"
                  src={obj.icon}
                  alt="Query Icon"
                  sx={{ width: 28, height: 28, ml: 1, mr: 2 }}
                />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    color: 'rgba(0, 0, 0, 0.8)',
                    fontWeight: 450,
                    textAlign: 'left',
                    minWidth: '180px',
                    maxWidth: '230px',
                    ml: '-5px',
                    fontSize: '14px',
                  }}
                >
                  {obj.text}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '98%', maxWidth: '100%', mb: 4, height: '280px' }}>
      {renderLayer(carouselItems[0], topRef, 'top')}
      {renderLayer(carouselItems[1], middleRef, 'middle')}
      {renderLayer(carouselItems[2], bottomRef, 'bottom')}
    </Box>
  );
};

export default QueryCarousel;